import React from "react";
import { ArrowRight, Building2, Briefcase, Users } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const archetypes = [{
  title: "Wealth Building and Asset Protection",
  icon: Users,
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1563866/img/hni_lifestyle_portrait.png",
  description: "Preserving wealth across generations with sophisticated tax and estate strategies.",
  needs: ["Advanced Tax-Strategy Consulting", "Private Life Insurance Solutions", "Comprehensive Estate Planning", "Legacy Preservation"]
}, {
  title: "Real Estate Investors",
  icon: Building2,
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1563866/img/modern_building_facade.png",
  description: "Maximizing asset value and cash flow through specialized investment structures.",
  needs: ["1031 Exchange Strategies", "Cost Segregation Analysis", "Portfolio Liquidity Solutions", "Asset Protection Trusts"]
}, {
  title: "Small Business Owners",
  icon: Briefcase,
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1563866/img/executive_boardroom_meeting.png",
  description: "Integrating business growth with personal wealth for a seamless financial future.",
  needs: ["Executive Benefit Planning", "Business Succession Strategies", "Tax-Efficient Exit Planning", "Key Person Insurance"]
}];
export default function ClientArchetypes() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section id="client-archetypes" className="dark bg-background text-foreground py-24 relative overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {}
        <div ref={ref} className={`max-w-3xl mx-auto text-center mb-16 transition-all duration-700 transform ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <h2 className="text-4xl md:text-5xl font-serif font-medium mb-6 tracking-tight">
            Who We Serve
          </h2>
          <p className="text-lg text-muted-foreground font-light leading-relaxed">
            Cyradon Capital provides specialized guidance for those with complex financial landscapes. 
            Our expertise is tailored to the unique demands of wealth creators in the Carolinas.
          </p>
        </div>

        {}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {archetypes.map((item, i) => <div key={i} data-index={i} className={`group h-[500px] perspective-1000 transition-all duration-700 delay-${i * 100} ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
              <Card className="relative h-full w-full overflow-hidden border-none rounded-xl bg-transparent shadow-2xl transition-transform duration-500 ease-out group-hover:scale-[1.02]">
                {}
                <div className="absolute inset-0 z-0">
                  <img src={item.image} alt={item.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
                </div>

                {}
                <div className="absolute inset-0 z-10 bg-background/80 transition-colors duration-500 group-hover:bg-background/60" />

                {}
                <CardContent className="relative z-20 h-full flex flex-col justify-end p-8 transition-all duration-500 group-hover:-translate-y-4">
                  <div className="mb-4">
                    <div className="inline-flex items-center justify-center size-12 rounded-full bg-primary/10 text-primary mb-4 backdrop-blur-sm border border-primary/20">
                      <item.icon className="size-6" />
                    </div>
                    <h3 className="text-2xl font-serif font-semibold text-foreground mb-2">
                      {item.title}
                    </h3>
                    <p className="text-muted-foreground text-sm leading-relaxed mb-6 group-hover:text-foreground/90 transition-colors">
                      {item.description}
                    </p>
                  </div>

                  {}
                  <div className="grid grid-rows-[0fr] group-hover:grid-rows-[1fr] transition-[grid-template-rows] duration-500 ease-in-out">
                    <div className="overflow-hidden">
                      <div className="pt-4 border-t border-primary/20 opacity-0 group-hover:opacity-100 transition-opacity duration-700 delay-100">
                        <h4 className="text-xs uppercase tracking-widest text-muted-foreground mb-3 font-medium">
                          Specific Strategic Needs
                        </h4>
                        <ul className="space-y-2">
                          {item.needs.map((need, idx) => <li key={idx} className="flex items-start gap-2 text-sm text-primary font-medium">
                              <span className="mt-1.5 size-1 rounded-full bg-primary shrink-0" />
                              {need}
                            </li>)}
                        </ul>
                        <div className="mt-6">
                          <Button variant="link" className="p-0 h-auto text-white hover:text-primary transition-colors group/btn" asChild>
                            <Link to="/WealthArchitecturePage">
                              Explore Solutions 
                              <ArrowRight className="ml-2 size-4 transition-transform group-hover/btn:translate-x-1" />
                            </Link>
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>)}
        </div>
      </div>
    </section>;
}